import React, { useEffect, useState } from 'react';
import { SHARE_FORMATS, drawShareCard, loadBanner } from './shareCardImage';
import { formatDateTimeRange } from './formatDate';
import { richTextToPlain } from './richText';

// e.g. "Sat 10.10.2026 ✶ 15:00"
function formatDateLabel(dateTime, endDateTime) {
  const date = new Date(dateTime);
  if (Number.isNaN(date.getTime())) return '';
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
  return `${weekday} ${formatDateTimeRange(date, endDateTime)}`;
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    } catch (error) {
      // Thrown when a cross-origin banner "taints" the canvas.
      resolve(null);
    }
  });
}

async function renderCard(details, format) {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const [banner, logo] = await Promise.all([
    loadBanner(details.bannerImage),
    loadBanner(`${process.env.PUBLIC_URL || ''}/frolik-logo.png`),
  ]);

  if (!drawShareCard(canvas, details, format, banner, logo)) return null;
  let blob = await canvasToBlob(canvas);

  if (!blob && banner) {
    // The banner blocked export; fall back to a card without it.
    drawShareCard(canvas, details, format, null, logo);
    blob = await canvasToBlob(canvas);
  }
  return blob;
}

export default function ShareCard({ event, url }) {
  const [format, setFormat] = useState('story');
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [message, setMessage] = useState('');

  const title = event.title || 'Untitled event';
  const shareText = `${title} on Frolik`;
  const fileName = `frolik-${event.slug || 'event'}-${format}.png`;
  const isPending = event.status === 'pending';
  const isRejected = event.status === 'rejected';

  const details = {
    title,
    slug: event.slug,
    dateLabel: formatDateLabel(event.dateTime, event.endDateTime),
    location: event.location,
    // The card is an image, so it shows the description as plain text.
    description: richTextToPlain(event.description),
    isPublic: event.isPublic !== false && event.public !== false,
    url,
    backgroundColor: event.backgroundColor,
    bannerImage: event.bannerImage || event.banner,
  };
  const detailsKey = JSON.stringify(details);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    renderCard(JSON.parse(detailsKey), format).then((result) => {
      if (cancelled || !result) return;
      objectUrl = URL.createObjectURL(result);
      setBlob(result);
      setPreviewUrl(objectUrl);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [detailsKey, format]);

  const download = () => {
    if (!previewUrl) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const shareImage = async () => {
    setMessage('');
    if (!blob) return;

    const file = new File([blob], fileName, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title, text: `${shareText} ${url}` });
      } catch (error) {
        if (error && error.name !== 'AbortError') setMessage('Sharing failed. Try downloading the image instead.');
      }
      return;
    }

    download();
    setMessage('Image saved. Open Instagram or Telegram on your phone and post it from your photos.');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage('Link copied.');
    } catch (error) {
      setMessage(`Copy this link: ${url}`);
    }
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);

  return (
    <section className="share-card" aria-label="Share this event">
      <div className="share-card-header">
        <strong>Share your event</strong>
        <div className="share-card-formats" role="group" aria-label="Card size">
          {Object.entries(SHARE_FORMATS).map(([key, value]) => (
            <button
              key={key}
              type="button"
              className={format === key ? 'is-active' : ''}
              aria-pressed={format === key}
              onClick={() => setFormat(key)}
            >
              {value.label}
            </button>
          ))}
        </div>
      </div>

      {isPending && (
        <p className="share-card-note">
          Your event is waiting for review. The link starts working for others once it's approved, so
          you can save the card now and post it then.
        </p>
      )}
      {isRejected && (
        <p className="share-card-note">This event wasn't approved, so the link won't work for others.</p>
      )}

      <div className={`share-card-preview is-${format}`}>
        {previewUrl ? <img src={previewUrl} alt={`Share card for ${title}`} /> : <span>Preparing card…</span>}
      </div>

      <div className="share-card-link">
        <span>{url}</span>
        <button type="button" onClick={copyLink}>Copy link</button>
      </div>

      <div className="share-card-actions">
        <button type="button" className="is-primary" onClick={shareImage} disabled={!blob}>
          Share image (Instagram, stories…)
        </button>
        <a href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`} target="_blank" rel="noopener noreferrer">
          Telegram
        </a>
        <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`} target="_blank" rel="noopener noreferrer">
          WhatsApp
        </a>
        <button type="button" onClick={download} disabled={!previewUrl}>Download</button>
      </div>

      {message && <p className="share-card-message" role="status">{message}</p>}
    </section>
  );
}
